import { ComponentProps, ComponentType, createElement, lazy, useState } from 'react';

// Misma restricción que `React.lazy`: cualquier componente, con sus props.
type AnyComponent = ComponentType<any>;

export type PreloadableComponent<T extends AnyComponent> = ComponentType<ComponentProps<T>> & {
  /** Baja y evalúa el chunk. Idempotente: siempre devuelve la misma promesa (salvo que haya fallado). */
  preload: () => Promise<unknown>;
};

/**
 * `React.lazy` que, si el chunk ya llegó (porque alguien llamó `preload()` antes), se
 * renderiza en la misma pasada, sin suspender.
 *
 * Por qué importa: con `lazy` a secas el primer render SIEMPRE suspende (aunque el módulo
 * ya esté evaluado, el `import()` resuelve recién en un microtask), React pinta el fallback
 * y React 19 demora la vuelta del contenido hasta 300 ms después del último fallback que se
 * pintó en cualquier parte de la app (FALLBACK_THROTTLE_MS). En una carga en frío eso
 * corría el título de cada pantalla (el LCP) ~200–400 ms, con el chunk ya descargado.
 *
 * El componente decide UNA vez, al montar, si usa el módulo ya resuelto o el lazy: cambiar
 * de tipo después remontaría el árbol.
 */
export function lazyWithPreload<T extends AnyComponent>(
  factory: () => Promise<{ default: T }>
): PreloadableComponent<T> {
  let loaded: T | undefined;
  let pending: Promise<{ default: T }> | undefined;

  const load = () => {
    if (!pending) {
      const attempt = factory().then((module) => {
        loaded = module.default;
        return module;
      });
      pending = attempt;
      // Un chunk que falló (red, deploy nuevo) se puede volver a pedir en el próximo preload.
      attempt.catch(() => {
        if (pending === attempt) pending = undefined;
      });
    }
    return pending;
  };

  const Lazy = lazy(load);

  const Preloadable = (props: ComponentProps<T>) => {
    const [Component] = useState<AnyComponent>(() => loaded ?? Lazy);
    return createElement(Component, props);
  };
  Preloadable.preload = load;
  return Preloadable;
}
